if (!customElements.get('techtees-product-tile')) {
  customElements.define('techtees-product-tile', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (target, name, fn) => target?.addEventListener(name, fn, { signal: this.controller.signal });
      this.media = this.querySelector('.tt-product-tile__media');
      this.dots = [...this.querySelectorAll('.tt-product-tile__dots span')];
      this.dots[0]?.setAttribute('aria-current', 'true');
      this.video = this.querySelector('video');
      this.motion = this.querySelector('.tt-product-tile__motion');
      this.suspended = false;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.finished = false;
      this.blocked = false;
      this.userPaused = false;
      this.slide = 0;
      on(this.media, 'scroll', () => {
        this.slide = Math.round(this.media.scrollLeft / this.media.clientWidth);
        this.dots.forEach((dot, i) => i === this.slide ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
        this.update();
      });
      if (!this.video || this.hasAttribute('data-static')) return;
      this.video.muted = true;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      on(this.reduced, 'change', () => this.update());
      on(this.pointer, 'change', () => this.update());
      const away = () => { if (this.pointer.matches && !this.hovered && !this.focused) this.reset(); this.update(); };
      on(this, 'pointerenter', () => { this.hovered = true; this.update(); });
      on(this, 'pointerleave', () => { this.hovered = false; away(); });
      on(this, 'focusin', () => { this.focused = true; this.update(); });
      on(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); away(); });
      on(this.video, 'ended', () => { this.finished = true; this.pending = false; this.motion.hidden = true; });
      on(this.video, 'error', () => { this.blocked = true; this.stop(); });
      on(this.motion, 'click', () => {
        this.userPaused = !this.userPaused;
        this.motion.setAttribute('aria-label', this.userPaused ? 'Play video' : 'Pause video');
        this.motion.toggleAttribute('data-paused', this.userPaused);
        this.update();
      });
      on(document, 'visibilitychange', () => { if (document.hidden) this.reset(); else this.update(); });
      on(window, 'pagehide', () => { this.suspended = true; this.reset(); });
      on(window, 'pageshow', () => { this.suspended = false; this.update(); });
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        this.near = entry.isIntersecting;
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
        if (!entry.isIntersecting) this.reset();
        else this.warm();
        this.update();
      }, { threshold: [0, 0.6] });
      this.observer.observe(this.media);
    }
    warm() {
      if (this.loaded || this.reduced.matches || this.blocked || !this.video) return;
      this.video.preload = 'auto';
      this.video.querySelectorAll('source[data-src]').forEach(source => source.setAttribute('src', source.dataset.src));
      this.video.load();
      this.loaded = true;
    }
    eligible() {
      return this.isConnected && (this.pointer.matches ? this.near !== false && (this.hovered || this.focused) : this.visible) && !document.hidden && !this.suspended && !this.reduced.matches && !this.slide && !this.userPaused && !this.blocked;
    }
    update() {
      if (!this.video || !this.reduced) return;
      if (this.reduced.matches) {
        this.stop();
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        if (this.loaded) this.video.load();
        this.loaded = false;
        this.motion.hidden = true;
        return;
      }
      if (!this.eligible()) { this.stop(); return; }
      if (this.finished || this.pending || !this.video.paused) return;
      if (!this.loaded) {
        this.video.querySelectorAll('source[data-src]').forEach(source => source.setAttribute('src', source.dataset.src));
        this.video.load();
        this.loaded = true;
      }
      this.video.loop = this.pointer.matches;
      this.pending = true;
      const attempt = this.attempt = (this.attempt || 0) + 1;
      Promise.resolve(this.video.play()).then(() => {
        if (attempt !== this.attempt) return;
        if (!this.eligible()) { this.video.pause(); return; }
        this.pending = false;
        this.motion.hidden = false;
        const show = () => { if (attempt === this.attempt) this.setAttribute('data-playing', ''); };
        if (this.video.readyState > 2) show(); else this.video.addEventListener('canplay', show, { once: true });
      }).catch(() => {
        if (attempt !== this.attempt) return;
        this.blocked = true;
        this.stop();
      });
    }
    stop() {
      this.attempt = (this.attempt || 0) + 1;
      this.pending = false;
      this.video.pause();
      this.removeAttribute('data-playing');
      if (!this.userPaused) this.motion.hidden = true;
    }
    reset() {
      this.stop();
      this.finished = false;
      this.blocked = false;
      this.video.currentTime = 0;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      if (this.video) {
        this.stop();
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        if (this.loaded) this.video.load();
        this.loaded = false;
      }
    }
  });
}
